import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { FileIdsValue, SelectMultipleValue } from '@kaizenplatform/hugot-entity';
import { Button, Divider, Grid, List as _List, ListItem, ListItemText, Typography } from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import { ClassAttributes, createElement, Fragment, MouseEventHandler, useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useMount, useToggle } from 'react-use';

import {
  DynamicSchemaDefinitionFieldType,
  DynamicSchemaDefinitionFileIds,
  DynamicSchemaDefinitionMultiLineText,
  DynamicSchemaDefinitionSection,
  DynamicSchemaDefinitionSelectOne,
  DynamicSchemaDefinitionSingleLineText,
  Option,
} from '../../../graphql/generated/graphql';
import theme from '../../../theme';
import { isEdge } from '../../../utility/isEdge';
import { EnhancedBatchDownload } from '../../connected/EnhancedBatchDownload';
import { SPACING_LARGE, SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { context as DependencyContext } from '../../context/dependency';
import { context as StateContext } from '../../context/state';
import { gridLayoutContainer, gridLayoutItem } from '../../styleUtility/gridLayout';
import LfToBr from '../primitive/LfToBr';
import Field from './AssetCategoryDetail/Field';
import SearchFileUploaderDialog from './SearchFileUploaderDialog';
import SearchFileUploaderTermsOfService from './SearchFileUploaderTermsOfService';

type Props = {
  className?: string;
  assetCategoryValue:
    | {
        values: Map<string, string | number | boolean | SelectMultipleValue | FileIdsValue | null>;
      }
    | undefined;
  assetCategorySections: Array<
    Pick<DynamicSchemaDefinitionSection, 'id' | 'meta' | 'label'> & {
      fields: Array<
        | Pick<DynamicSchemaDefinitionFileIds, 'key' | 'type'>
        | Pick<DynamicSchemaDefinitionMultiLineText, 'key' | 'type'>
        | Pick<DynamicSchemaDefinitionSelectOne, 'key' | 'type'>
        | Pick<DynamicSchemaDefinitionSingleLineText, 'key' | 'type'>
      >;
    }
  > | null;
  assetFiles: ReadonlyArray<FileReference>;
  selectedPricingMerchandiseOptions: Array<Pick<Option, 'name' | 'displayToOptimizer' | 'helpUrlForOptimizer'>>;
};

const ProjectBasicSettingDetail = ({
  className,
  assetCategoryValue,
  assetCategorySections,
  assetFiles,
  selectedPricingMerchandiseOptions,
}: Props & ClassAttributes<HTMLElement>) => {
  const [isOpenSearchAsset, toggleIsOpenSearchAsset] = useToggle(false);
  const [showTermsOfModal, setShowTermsOfModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const state = useContext(StateContext);
  const dependency = useContext(DependencyContext);
  const isAssetTermAgreed = state.searchAsset.isAssetTermAgreed;
  const onAssetFormTermAgreementsConfirm = () => {
    dependency.searchAssetUsecase.postAssetFormTermAgreements();
  };

  const onClickSearchAssetButton: MouseEventHandler = () => {
    if (!isEdge()) {
      toggleIsOpenSearchAsset();
      setShowTermsOfModal(true);
    }
  };

  const displayToOptimizerOptions = selectedPricingMerchandiseOptions.filter(
    (pricingMerchandiseOption) => pricingMerchandiseOption.displayToOptimizer,
  );

  useMount(async () => {
    await dependency.searchAssetUsecase.setAssetFormTermAgreements();
  });

  const downloadFileIds =
    assetCategoryValue != null && assetCategorySections != null
      ? assetCategorySections.reduce<ReadonlyArray<string>>((acc, cur) => {
          const fileIds = cur.fields
            .filter((field) => field.type == DynamicSchemaDefinitionFieldType.FileIds)
            .flatMap((field) => assetCategoryValue.values.get(field.key) as ReadonlyArray<string>);
          return [...acc, ...fileIds];
        }, [])
      : [];

  return (
    <Root className={className}>
      <Section>
        <Grid container justify="space-between">
          <Grid item>
            <SectionHeading variant="h6">
              <FormattedMessage id="project_basic_setting_detail.section_heading.assets" />
            </SectionHeading>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={onClickSearchAssetButton}
              disabled={isEdge()}
            >
              <FormattedMessage id="search_file_uploader.description" />
            </Button>
            {isEdge() && (
              <Typography color="primary">
                <FormattedMessage id="search_file_uploader.description.non_supported" />
              </Typography>
            )}
          </Grid>
        </Grid>

        <LabelAndValue>
          <Label>
            <FormattedMessage id="project_basic_setting_detail.assets.label.uploaded_files">
              {(label: string) => <LfToBr text={label} />}
            </FormattedMessage>
          </Label>
          <Value>
            {assetCategoryValue != null &&
              assetCategorySections != null &&
              assetCategorySections.map((section) => (
                <AssetCategorySection key={section.id}>
                  <Typography variant="h6">{section.label}</Typography>
                  {section.fields.map((field) => (
                    <Field
                      key={field.key}
                      field={field}
                      value={assetCategoryValue}
                      files={assetFiles}
                      meta={section.meta}
                    />
                  ))}
                </AssetCategorySection>
              ))}
            {assetFiles.length >= 2 ? <EnhancedBatchDownload fileIds={downloadFileIds} /> : null}
          </Value>
        </LabelAndValue>
      </Section>

      {displayToOptimizerOptions.length > 0 ? (
        <Fragment>
          <SectionDivider />
          <Section>
            <SectionHeading variant="h6">
              <FormattedMessage id="project_basic_setting_detail.section_heading.options" />
            </SectionHeading>
            <PricingMerchandiseOptions>
              {displayToOptimizerOptions.map((pricingMerchandiseOption, index) => (
                <ListItem key={index}>
                  <ListItemText
                    primary={
                      <Fragment>
                        <Typography variant="body2">
                          {pricingMerchandiseOption.name}
                          {pricingMerchandiseOption.helpUrlForOptimizer ? (
                            <HelpAnchor
                              href={pricingMerchandiseOption.helpUrlForOptimizer}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <HelpIcon />
                            </HelpAnchor>
                          ) : null}
                        </Typography>
                      </Fragment>
                    }
                  />
                </ListItem>
              ))}
            </PricingMerchandiseOptions>
          </Section>
        </Fragment>
      ) : null}

      <SearchFileUploaderDialog isOpen={isOpenSearchAsset} onClose={toggleIsOpenSearchAsset} region={state.region} />
      {!isAssetTermAgreed ? (
        <SearchFileUploaderTermsOfService
          isOpen={showTermsOfModal}
          setIsChecked={setIsChecked}
          onConfirm={() => {
            if (isChecked) {
              onAssetFormTermAgreementsConfirm();
              setIsChecked(false);
              setShowTermsOfModal(false);
            } else {
              setShowTermsOfModal(false);
            }
          }}
        />
      ) : null}
    </Root>
  );
};

const Root = styled('div')({});

const Section = styled('div')({
  marginBottom: SPACING_UNIT * 3,
});

const SectionDivider = styled(Divider)({
  marginBottom: SPACING_UNIT * 3,
});

const SectionHeading = styled(Typography)({
  marginBottom: SPACING_REGULAR,
});
SectionHeading.defaultProps = { variant: 'h6' };

export const LabelAndValue = styled('div')({
  ...gridLayoutContainer({
    areas: [['label', '.', 'value']],
    columns: ['20%', `${SPACING_UNIT * 3}px`, `calc(100% - 20% - ${SPACING_UNIT * 3}px)`],
    rows: ['auto'],
  }),
  marginBottom: SPACING_UNIT,
});

export const Label = styled(Typography)({
  ...gridLayoutItem('label'),
  textAlign: 'end',
});

export const Value = styled('div')({
  ...gridLayoutItem('value'),
});

export const ValueText = styled(Typography)({
  wordBreak: 'break-word' as any,
  hyphens: 'auto',
});

const PricingMerchandiseOptions = styled(_List)({});

const HelpAnchor = styled('a')({
  display: 'inline-flex',
  alignItems: 'center',
  verticalAlign: '-0.3rem',
  marginLeft: SPACING_UNIT,
  color: theme.palette.secondary.main,
  textDecoration: 'none',
});
const HelpIcon = styled(Help)({
  marginRight: 2,
  fontSize: 20,
});

const AssetCategorySection = styled('div')`
  margin-bottom: ${SPACING_LARGE}px;
`;

export default ProjectBasicSettingDetail;
